import { useCallback, useEffect, useRef } from 'react';

import { StorageManager, useApplicationState } from '@atlas/core';

import { POSTHOG_EVENTS } from 'common/constants';

import usePostHog from './usePostHog';

const QUICK_DEPOSIT_DELAY = 'quick_deposit_delay';

const useQuickDepositPostHog = ({ afterDelay, onCloseClick }) => {
  const postHog = usePostHog();
  const isSentRef = useRef(false);
  const isInitializedRef = useRef(false);
  const {
    account,
    wallet: { detailedBalance: { BONUS = 0, CASH = 0 } = {} },
  } = useApplicationState();

  const getBaseData = useCallback(() => {
    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);
    const { delay = 0 } = delayInfo ? JSON.parse(delayInfo) : {};

    return {
      after_delay: afterDelay !== undefined ? afterDelay : delay,
      client_id: account.id,
      page_refer: window.location.pathname,
      user_bonus_balance: BONUS,
      user_real_balance: CASH,
    };
  }, [account.id, afterDelay, BONUS, CASH]);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initData = {
      ...getBaseData(),
      amount: null,
      bonus_id: null,
      cta_clicked: null,
      manual_closed_timestamp: null,
      payment_method: null,
      timestamp: Date.now(),
    };

    postHog.concat(POSTHOG_EVENTS.QUICK_DEP_POP_UP_SHOWN, initData);
  }, [getBaseData, postHog]);

  useEffect(() => {
    return () => {
      if (!isSentRef.current && window.posthog) {
        postHog.send(POSTHOG_EVENTS.QUICK_DEP_POP_UP_SHOWN);
      }
    };
  }, []);

  const handleCloseClick = useCallback(
    (withCloseCallback = true) => {
      if (!isSentRef.current) {
        postHog.send(POSTHOG_EVENTS.QUICK_DEP_POP_UP_SHOWN, {
          manual_closed_timestamp: Date.now(),
        });
        isSentRef.current = true;
      }
      if (withCloseCallback) {
        onCloseClick();
      }
    },
    [postHog, onCloseClick],
  );

  const handleDepositStart = useCallback(
    startData => {
      isSentRef.current = true;
      postHog.concat(POSTHOG_EVENTS.QUICK_DEP_POP_UP_SHOWN, { ...getBaseData(), ...startData });
    },
    [getBaseData, postHog],
  );

  const handleDepositSuccess = useCallback(
    finalData => {
      postHog.send(POSTHOG_EVENTS.QUICK_DEP_POP_UP_SHOWN, finalData);
      isSentRef.current = true;
    },
    [postHog],
  );

  const handleDepositLoaderShown = useCallback(
    ({ amount, currency, locale, method }) => {
      postHog.send(POSTHOG_EVENTS.DEPOSIT_LOADER_SHOWN, { amount, currency, locale, method });
    },
    [postHog],
  );

  const handleDepositRedirectSuccess = useCallback(
    ({ amount, currency, locale, method, t_wait_ms }) => {
      postHog.send(POSTHOG_EVENTS.DEPOSIT_REDIRECT_SUCCESS, {
        amount,
        currency,
        locale,
        method,
        t_wait_ms,
      });
    },
    [postHog],
  );

  return {
    handleCloseClick,
    handleDepositLoaderShown,
    handleDepositRedirectSuccess,
    handleDepositStart,
    handleDepositSuccess,
  };
};

export default useQuickDepositPostHog;
